import { Component, computed, inject, signal, DestroyRef, effect, TemplateRef } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { DatePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { AccordionModule } from '@openng/optimus-ui/accordion';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbilityService,
  ApiCallingV2Service,
  CurrencyFormatPipe,
  LotType,
  NgbModalService,
  rxResourceFixed
} from '@zayan-erp/shared';
import { HttpErrorResponse } from '@angular/common/http';
import { of, tap, map, Observable, Subject } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { SvgIconsComponent, DynamicRouteHandlingService, PhoneNumbersDisplayComponent } from '@zayan-erp/shared';
import {
  Elevation,
  ElevationService,
  Lot,
  LotChangeWarnings,
  LotsService,
  SalesOffice
} from '@zayan-erp/home-building';
import { DealsProject } from '../../models/deals-project.models';
import { SalesApsService, SalesService, ProspectFormComponent } from '@zayan-erp/sales';
import { Deal, ModelImport } from '../../models/deal.models';
import { SpecLotModelOption } from '../../models/build-spec-models.models';
import { Prospect } from '../../models/prospect.models';
import { SalesOfficeService } from '../../../../../home-building/src/lib/services/sales-office.service';
import { ProjectsService } from '@zayan-erp/home-building';
import { AgencyRealtorService } from '../../services/agency-realtor.service';
import { DepositService } from '../../services/deposit.service';
import { AgencyRealtor } from '../../models/agency-realtor.models';
import { OPEN_LOT_STATUS_ID } from '../../constants/deals.constants';
import { TableModule } from '@openng/optimus-ui/table';
import { ConfirmDialogModule } from '@openng/optimus-ui/confirmdialog';
import { Popover, PopoverModule } from '@openng/optimus-ui/popover';
import { viewChild } from '@angular/core';
import { ConfirmationService } from '@openng/optimus-ui/api';
import { MessageModule } from '@openng/optimus-ui/message';
import { CheckboxModule } from '@openng/optimus-ui/checkbox';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'deals-form',
  standalone: true,
  imports: [
    NgTemplateOutlet,
    ReactiveFormsModule,
    FormsModule,
    SvgIconsComponent,
    AccordionModule,
    DatePipe,
    NgClass,
    TableModule,
    PhoneNumbersDisplayComponent,
    ConfirmDialogModule,
    PopoverModule,
    MessageModule,
    CheckboxModule,
    CurrencyFormatPipe,
    ProspectFormComponent
  ],
  providers: [],
  templateUrl: './deals-form.component.html',
  styleUrl: './deals-form.component.scss'
})
export class DealsFormComponent {
  // =========================
  // Dependencies
  // =========================
  #fb = inject(FormBuilder);
  #router = inject(Router);
  #destroyRef = inject(DestroyRef);
  #dynamicRouteHandling = inject(DynamicRouteHandlingService);
  #projectsService = inject(ProjectsService);
  #salesApsService = inject(SalesApsService);
  #salesOfficeService = inject(SalesOfficeService);
  #salesService = inject(SalesService);
  #agencyRealtorService = inject(AgencyRealtorService);
  #depositService = inject(DepositService);
  #api = inject(ApiCallingV2Service);
  #abilityService = inject(AbilityService);
  #elevationService = inject(ElevationService);
  #lotsService = inject(LotsService);
  #activatedRoute = inject(ActivatedRoute);
  #confirmationService = inject(ConfirmationService);
  #modalService = inject(NgbModal);
  #ngbModalService = inject(NgbModalService);
  #addProspectModalRef: NgbModalRef | null = null;
  addProspectModal = viewChild.required<TemplateRef<unknown>>('addProspectModal');
  onSubmitSubject = new Subject<void>();
  onSubmit$ = this.onSubmitSubject.asObservable();

  // =========================
  // Overlay + dialog state
  // =========================
  violatedRules: string[] = [];
  combinedEligibilityPopover = viewChild<Popover>('combinedEligibilityPopover');
  eligibilityResult = computed(() => this.eligibilityResource.value());
  combinedEligibilityRulesMessage = computed(() => this.eligibilityResult()?.violatedRules ?? []);
  protected readonly String = String;

  // =========================
  // Form + selection state
  // =========================
  salesOfficeId = toSignal(this.#salesService.saleOfficeId$, { initialValue: null }) ?? null;
  dealForm = this.#fb.group({
    salesOfficeId: ['', Validators.required],
    prospectId: ['', Validators.required],
    projectId: ['', Validators.required],
    lotId: ['', Validators.required],
    modelId: ['', Validators.required],
    elevationId: ['', Validators.required],
    realtorAgentId: ['']
  });
  activePanel = signal<string>('1');
  searchTerms = {
    prospect: signal(''),
    lot: signal(''),
    model: signal(''),
    elevation: signal(''),
    realtorAgent: signal(''),
    project: signal('')
  };
  selected = {
    salesOffice: signal<SalesOffice | null>(null),
    project: signal<string | null>(this.#activatedRoute.snapshot.queryParamMap.get('projectId') ?? null),
    prospect: signal<Prospect | null>(null),
    lot: signal<string | null>(null),
    model: signal<ModelImport | null>(null),
    elevation: signal<Elevation | null>(null),
    realtorAgent: signal<AgencyRealtor | null>(null)
  };

  // =========================
  // Data resources (UI flow order)
  // =========================
  // Step 1: Prospect
  prospectsResource = rxResourceFixed({
    params: () => this.selected.salesOffice()?.id ?? null,
    defaultValue: [] as Prospect[],
    stream: ({ params: salesOfficeId }) =>
      !salesOfficeId
        ? of([] as Prospect[])
        : this.#salesApsService
            .getProspects(1, 1000, salesOfficeId)
            .pipe(map((res: any) => (Array.isArray(res) ? res : (res?.items ?? []))))
  });

  // Right sidebar: Past APS for selected prospect
  prospectDealsResource = rxResourceFixed({
    params: () => this.selected.prospect()?.id ?? null,
    defaultValue: [] as Deal[],
    stream: ({ params: prospectId }) =>
      prospectId
        ? this.#salesApsService
            .getProspectDeals(prospectId)
            .pipe(map((res: Deal[] | { items?: Deal[] }) => (Array.isArray(res) ? res : (res.items ?? []))))
        : of([] as Deal[])
  });

  // Step 2: Lot
  lotsResource = rxResourceFixed({
    params: () => ({
      projectId: this.selected.project(),
      lotStatus: OPEN_LOT_STATUS_ID
    }),
    defaultValue: [] as Lot[],
    stream: ({ params }) => {
      const { projectId, lotStatus } = params;
      return projectId
        ? this.#projectsService.getProjectLots(projectId, false, lotStatus).pipe(
            tap(lots => {
              const currentLot = this.selected.lot();
              if (currentLot && !lots.some(lot => lot.id === currentLot)) {
                this.selected.lot.set(null);
                this.#skippedDepositOwners.set(new Set());
              }
            })
          )
        : of([] as Lot[]);
    }
  });

  // A Spec lot offers the same project-wide list as Build Spec, so the sale keeps the
  // model/elevation chosen there; every other lot type stays on its siting analysis.
  isSpecLot = computed(() => this.lots().find(l => l.id === this.selected.lot())?.lotTypeId === LotType.Spec);
  specModelsResource = rxResourceFixed({
    params: () => (this.isSpecLot() ? this.selected.project() : null),
    defaultValue: [] as SpecLotModelOption[],
    stream: ({ params: projectId }) =>
      projectId ? this.#salesService.readSpecLotModelElevations(projectId) : of([] as SpecLotModelOption[])
  });
  specModels = computed(() => (this.isSpecLot() ? this.specModelsResource.value() : null));

  // Step 3: Model
  modelsResource = rxResourceFixed({
    params: () => {
      const lotId = this.selected.lot();
      const selectedLot = this.lots().find(l => l.id === lotId) ?? null;
      return {
        lotId,
        lotTypeId: selectedLot?.lotTypeId,
        selectedModelId: selectedLot?.selectedModelId,
        specModels: this.specModels()
      };
    },
    defaultValue: [] as ModelImport[],
    stream: ({ params }) =>
      params.lotId
        ? this.#modelsForLot$(params.lotId, params.specModels).pipe(
            tap((models: ModelImport[]) => {
              const currentModel = this.selected.model();
              if (currentModel && !models.some(m => m.id === currentModel.id)) {
                this.selected.model.set(null);
              }

              console.log('params.selectedModelId', params.selectedModelId);
              if (params.selectedModelId != null) {
                const model = models.find(m => m.modelId === params.selectedModelId);
                if (model != null) {
                  this.selected.model.set(model);
                  this.activePanel.set('');
                }
              }
            })
          )
        : of([] as ModelImport[])
  });

  // Step 4: Elevation
  elevationsResource = rxResourceFixed({
    params: () => {
      const lotId = this.selected.lot();
      const selectedLot = this.lots().find(l => l.id === lotId) ?? null;
      return {
        lotId,
        modelId: this.selected.model()?.id,
        lotTypeId: selectedLot?.lotTypeId,
        selectedElevationId: selectedLot?.selectedElevationId,
        specModels: this.specModels()
      };
    },
    defaultValue: [],
    stream: ({ params }) => {
      const { lotId, modelId } = params;
      return lotId && modelId
        ? this.#elevationsForModel$(lotId, modelId, params.specModels).pipe(
            tap((elevations: Elevation[]) => {
              const currentElevation = this.selected.elevation();
              if (currentElevation && !elevations.some(e => e.id === currentElevation.id)) {
                this.selected.elevation.set(null);
              }
              if (params.selectedElevationId != null) {
                const elevation = elevations.find(e => e.id === params.selectedElevationId);
                if (elevation != null) {
                  this.selected.elevation.set(elevation);
                  if (this.selected.prospect()?.realtorId) {
                    this.selected.realtorAgent.set(
                      this.realtorAgents().find(r => r.id === this.selected.prospect()?.realtorId) ?? null
                    );
                    this.activePanel.set('');
                  } else {
                    this.activePanel.set('6');
                  }
                }
              }
            })
          )
        : of([]);
    }
  });

  // Step 5: Realtor Agent
  realtorAgentsResource = rxResourceFixed({
    params: () => ({
      modelId: this.selected.model()?.id ?? null
    }),
    defaultValue: [] as AgencyRealtor[],
    stream: ({ params }) => {
      const { modelId } = params;
      if (!modelId) return of([] as AgencyRealtor[]);
      return this.#agencyRealtorService.getAgencyRealtors({ pageNumber: 1, pageSize: 1000 }).pipe(
        map((res: any) => {
          return (res.items ?? []) as AgencyRealtor[];
        })
      );
    }
  });

  eligibilityResource = rxResourceFixed({
    params: () => {
      const lotId = this.selected.lot();
      const modelId = this.selected.model()?.modelId ?? null;
      const elevationId = this.selected.elevation()?.id ?? null;
      return { lotId, modelId, elevationId };
    },
    defaultValue: null as { isEligible: boolean; violatedRules: string[] } | null,
    stream: ({ params }) => {
      const { lotId, modelId, elevationId } = params;
      if (!lotId) {
        return of(null);
      }
      return this.#salesApsService.checkRuntimeEligibility({ lotId, modelId, elevationId, scope: 1 });
    }
  });

  lotWarnings = rxResourceFixed({
    params: () => ({ lotId: this.selected.lot() ?? '' }),
    defaultValue: null,
    stream: ({ params }) => (params.lotId ? this.#lotsService.getLotChangeWarnings(params.lotId) : of(null))
  });

  // Leftover deposits from a cancelled deal are taken over by default; the buyer's deal ids unticked here are left alone.
  #skippedDepositOwners = signal<ReadonlySet<string>>(new Set());

  // Adopt needs the same permission the old Deposits-tab buttons did; without it the form only shows the note.
  canUseLeftoverDeposits = computed(() => this.#abilityService.ability.can('write-sales-deals', 'deals'));

  // Deposits a live deal left on its old lot (old lot changes only) are neither offered nor shown; the clean-up moves them.
  cancelledDealDeposits = computed(() =>
    (this.lotWarnings.value()?.leftoverDeposits ?? []).filter(group => !group.ownerIsLive)
  );

  unpaidDepositIds(group: LotChangeWarnings['leftoverDeposits'][number]): string[] {
    return group.depositIds.filter(id => !group.paidDepositIds.includes(id));
  }

  unpaidDepositsTotal(group: LotChangeWarnings['leftoverDeposits'][number]): number {
    return Math.round((group.total - group.paidTotal) * 100) / 100;
  }

  isUsingDeposits(group: LotChangeWarnings['leftoverDeposits'][number]): boolean {
    return !this.#skippedDepositOwners().has(group.dealId);
  }

  setUsingDeposits(group: LotChangeWarnings['leftoverDeposits'][number], use: boolean): void {
    const skipped = new Set(this.#skippedDepositOwners());
    if (use) skipped.delete(group.dealId);
    else skipped.add(group.dealId);
    this.#skippedDepositOwners.set(skipped);
  }

  /** The unpaid deposits of every ticked group: what the new deal takes over once it exists. */
  #depositIdsToUse(): string[] {
    if (!this.canUseLeftoverDeposits()) return [];
    return this.cancelledDealDeposits()
      .filter(group => this.isUsingDeposits(group))
      .flatMap(group => this.unpaidDepositIds(group));
  }

  // =========================
  // Computed collections
  // =========================
  prospects = computed<Prospect[]>(() => this.prospectsResource.value() ?? []);
  prospectDeals = computed<Deal[]>(() => this.prospectDealsResource.value() ?? []);
  lots = computed(() => this.lotsResource.value() ?? []);
  models = computed<ModelImport[]>(() => this.modelsResource.value() ?? []);
  elevations = computed(() => this.elevationsResource.value() ?? []);
  realtorAgents = computed(() => this.realtorAgentsResource.value() ?? []);

  // Summary card computed values
  projects = signal<DealsProject[]>([]);
  selectedProjectData = computed(() => {
    const sel = this.selected.project();
    if (!sel) return null;
    return this.projects().find(p => p.id === sel || p.projectId === sel) ?? null;
  });

  selectedProspectFullName = computed(() => {
    const p = this.selected.prospect();
    return p?.firstName && p?.lastName ? `${p.firstName} ${p.lastName}` : '-';
  });
  selectedProspectPhone = computed(() => this.selected.prospect()?.phones?.[0]?.phoneNumber ?? 'No Phone');

  selectedLotData = computed(() => this.lots().find(l => l.id === this.selected.lot()) ?? null);
  selectedModelData = computed(() => this.selected.model()?.modelName ?? null);
  selectedElevationData = computed(() => this.selected.elevation()?.elevationCode ?? null);
  selectedRealtorAgentData = computed(() => {
    const r = this.selected.realtorAgent();
    if (!r) return null;
    return [r.firstName, r.lastName].filter(Boolean).join(' ') || null;
  });

  // Search-filtered lists
  filteredProspects = computed(() => {
    const term = this.searchTerms.prospect().toLowerCase();
    const officeId = this.selected.salesOffice()?.id;
    let list = this.prospects();
    if (officeId) {
      list = list.filter(p => p.salesOfficeId === officeId);
    }
    if (term) {
      list = list.filter(
        p =>
          `${p.firstName ?? ''} ${p.lastName ?? ''}`.toLowerCase().includes(term) ||
          p.email?.toLowerCase().includes(term)
      );
    }
    return list.map(p => ({
      ...p,
      fullName: p.firstName && p.lastName ? `${p.firstName} ${p.lastName}` : '-',
      phone: p.phones?.[0]?.phoneNumber || 'No Phone'
    }));
  });
  filteredProjects = computed(() => {
    const term = this.searchTerms.project().toLowerCase();
    const officeId = this.selected.salesOffice()?.id;
    let list = this.projects();
    if (officeId) {
      list = list.filter(p => {
        const projectOfficeId = p.salesOfficeId;
        return !projectOfficeId || projectOfficeId === officeId;
      });
    }
    if (term) {
      list = list.filter(
        p => p.projectCode?.toLowerCase().includes(term) || p.projectName?.toLowerCase().includes(term)
      );
    }
    return list;
  });
  filteredLots = computed(() =>
    this.filterList(
      this.lots(),
      this.searchTerms.lot(),
      lot => `${lot.lotNo ?? ''} ${lot.projectCode ?? ''} ${lot.buyerName ?? ''}`
    )
  );
  filteredModels = computed(() =>
    this.filterList(this.models(), this.searchTerms.model(), m => `${m.modelName ?? ''} ${m.style ?? ''}`)
  );
  filteredElevations = computed(() =>
    this.filterList(
      this.elevations(),
      this.searchTerms.elevation(),
      e => `${e.elevationCode ?? ''} ${e.description ?? ''}`
    )
  );
  filteredRealtorAgents = computed(
    () =>
      this.filterList(
        this.realtorAgents(),
        this.searchTerms.realtorAgent(),
        r =>
          `${r.firstName ?? ''} ${r.lastName ?? ''} ${r.email ?? ''} ${r.phones?.map((p: any) => p.phoneNumber).join(' ') ?? ''}`
      ) as AgencyRealtor[]
  );
  isCreateEnabled = computed(
    () =>
      !!this.selected.salesOffice() &&
      !!this.selected.prospect() &&
      !!this.selected.project() &&
      !!this.selected.lot() &&
      !!this.selected.model() &&
      !!this.selected.elevation()
  );
  // =========================
  // Initialization + form synchronization
  // =========================
  constructor() {
    // Auto-select sales office from localStorage
    const storedSalesOffice = this.salesOfficeId();
    if (storedSalesOffice) {
      // Find the sales office from the service to get full details
      this.#salesOfficeService
        .getSalesOffice()
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe({
          next: offices => {
            const office = offices.find(o => o.id === storedSalesOffice);
            if (office) {
              this.selected.salesOffice.set(office);
              // Load projects for the selected sales office
              this.#salesOfficeService
                .getSalesOfficeProjects(office.id)
                .pipe(takeUntilDestroyed(this.#destroyRef))
                .subscribe({
                  next: res => this.projects.set(res as DealsProject[]),
                  error: () => this.projects.set([])
                });
            }
          }
        });
    }

    this.#dynamicRouteHandling.submitButton$
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.onSubmit());
    effect(() => {
      this.dealForm.patchValue(
        {
          salesOfficeId: this.selected.salesOffice()?.id ?? null,
          prospectId: this.selected.prospect()?.id ?? null,
          projectId: this.selected.project(),
          lotId: this.selected.lot(),
          modelId: this.selected.model()?.modelId ?? null,
          elevationId: this.selected.elevation()?.id ?? null,
          realtorAgentId: this.selected.realtorAgent()?.id ?? null
        },
        { emitEvent: false }
      );
    });
  }

  // =========================
  // Model / elevation sources
  // =========================
  #modelsForLot$(lotId: string, specModels: SpecLotModelOption[] | null): Observable<ModelImport[]> {
    if (!specModels) {
      return this.#salesApsService.getModels(lotId);
    }
    const projectId = this.selected.project() ?? '';
    return of(
      specModels.map(
        m =>
          ({
            id: m.modelId,
            modelId: m.modelId,
            modelName: m.modelName,
            bedrooms: m.bedrooms,
            style: m.style,
            notes: m.notes,
            projectId
          }) as ModelImport
      )
    );
  }

  #elevationsForModel$(
    lotId: string,
    modelId: string,
    specModels: SpecLotModelOption[] | null
  ): Observable<Elevation[]> {
    if (!specModels) {
      return this.#elevationService.getLotModelElevations(lotId, modelId);
    }
    const elevations = specModels.find(m => m.modelId === modelId)?.elevations ?? [];
    return of(
      elevations.map(
        e =>
          ({
            id: e.id,
            elevationCode: e.elevationCode,
            description: e.description ?? '',
            area: e.area,
            modelId
          }) as Elevation
      )
    );
  }

  // =========================
  // Step selection handlers (UI flow order)
  // =========================
  onProspectSelect(prospect: Prospect): void {
    this.selected.prospect.set(prospect);
    // Projects are already loaded when sales office is selected from localStorage
    // Only load projects if they haven't been loaded yet
    if (this.projects().length === 0 && this.selected.salesOffice()?.id) {
      this.#salesOfficeService
        .getSalesOfficeProjects(this.selected.salesOffice()!.id)
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe({
          next: res => this.projects.set(res as DealsProject[]),
          error: () => this.projects.set([])
        });
    }
    this.activePanel.set('2');
  }
  onProjectSelect(project: DealsProject): void {
    const pid = project.projectId ?? project.id ?? null;
    this.selected.project.set(pid);
    this.selected.lot.set(null);
    this.#skippedDepositOwners.set(new Set());
    this.selected.model.set(null);
    this.selected.elevation.set(null);
    this.selected.realtorAgent.set(null);
    this.activePanel.set('3');
  }
  onLotSelect(lot: Lot): void {
    this.selected.lot.set(lot.id);
    this.#skippedDepositOwners.set(new Set());
    this.selected.model.set(null);
    this.selected.elevation.set(null);
    this.selected.realtorAgent.set(null);
    this.activePanel.set('4');
  }
  onModelSelect(model: ModelImport): void {
    this.selected.model.set(model);
    this.selected.elevation.set(null);
    this.selected.realtorAgent.set(null);
    this.activePanel.set('5');
  }
  onElevationSelect(elevation: Elevation): void {
    this.selected.elevation.set(elevation);
    if (this.selected.prospect()?.realtorId) {
      this.selected.realtorAgent.set(
        this.realtorAgents().find(r => r.id === this.selected.prospect()?.realtorId) ?? null
      );
      this.activePanel.set('');
    } else {
      this.activePanel.set('6');
    }
  }
  onRealtorAgentSelect(realtorAgent: AgencyRealtor): void {
    this.selected.realtorAgent.set(realtorAgent);
    this.activePanel.set('');
  }
  // =========================
  // Supporting UI actions
  // =========================
  navigateToDealDetail(deal: Deal): void {
    if (deal?.id) {
      window.open(`/sales/aps/${deal.id}`, '_blank');
    }
  }
  getStatusClass(status: string): string {
    if (!status) return 'badge bg-secondary-subtle fs-12 fw-500 text-secondary';
    const statusLower = status.toLowerCase();
    const statusMap: Record<string, string> = {
      selection: 'badge bg-success-subtle fs-12 fw-500 text-bg-light',
      contract: 'badge bg-info-subtle fs-12 fw-500 text-info',
      pending: 'badge bg-warning-subtle fs-12 fw-500 text-warning',
      cancel: 'badge bg-danger-subtle fs-12 fw-500 text-danger'
    };
    for (const [key, className] of Object.entries(statusMap)) {
      if (statusLower.includes(key)) return className;
    }
    return 'badge bg-secondary-subtle fs-12 fw-500 text-secondary';
  }

  // =========================
  // Submission flow
  // =========================
  onSubmit(): void {
    this.violatedRules = [];
    if (!this.isCreateEnabled() || !this.dealForm.valid) {
      this.dealForm.markAllAsTouched();
      return;
    }
    const value = this.dealForm.getRawValue() as Record<string, string>;
    const lotId = value?.['lotId'];
    const modelId = value?.['modelId'];
    const elevationId = value?.['elevationId'];
    if (!lotId || !modelId || !elevationId) {
      this.#doCreateDeal();
      return;
    }

    const current = this.eligibilityResult();
    if (current?.isEligible) {
      this.#doCreateDeal();
      return;
    }

    this.#salesApsService
      .checkRuntimeEligibility({ lotId, modelId, elevationId, scope: 1 })
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: eligibility => {
          if (eligibility.isEligible) {
            this.#doCreateDeal();
            return;
          }

          this.violatedRules = eligibility.violatedRules ?? [];
          this.#confirmationService.confirm({
            icon: 'confirm-icon',
            header: 'Rule Violations',
            acceptLabel: 'Create Anyway',
            rejectLabel: 'Cancel',
            message:
              'This deal is violating the rules. Are you sure you want to continue?' +
              `<ul><li> The selected model violates the following rule(s): ${eligibility.violatedRules.join('<br /><li>')}</li></ul>`,
            accept: () => this.#doCreateDeal(),
            reject: () => undefined
          });
        }
      });
  }

  #doCreateDeal(): void {
    // Read before the create call: the warnings belong to the lot picked now.
    const depositIds = this.#depositIdsToUse();
    this.#salesApsService
      .createDeal(this.dealForm.value as any)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: res => {
          const dealId = res?.id ?? res?.dealId ?? res?.salesAgents?.id;
          if (!dealId || depositIds.length === 0) {
            this.#openCreatedDeal(dealId);
            return;
          }
          // A refused adopt must not strand the user on the form: the deal exists, so show why and open it anyway.
          this.#depositService
            .adoptDeposits(dealId, depositIds)
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe({
              next: () => this.#openCreatedDeal(dealId),
              error: (error: HttpErrorResponse) => {
                this.#api.showMessage(
                  error.error?.errors?.[0] ?? error.error?.message ?? 'Could not use the deposits for this deal.',
                  'error'
                );
                this.#openCreatedDeal(dealId);
              }
            });
        }
      });
  }

  #openCreatedDeal(dealId: string | undefined): void {
    this.#router.navigate(['/sales/aps'], { queryParamsHandling: 'preserve' });
    setTimeout(() => {
      if (dealId) {
        this.#router.navigate(['/sales/aps', dealId, 'overview'], { queryParamsHandling: 'preserve' });
      }
    }, 500);
  }

  filterList<T>(items: T[], term: string, selector: (item: T) => string): T[] {
    if (!term) return items;
    const lowerTerm = term.toLowerCase();
    return items.filter(item => selector(item).toLowerCase().includes(lowerTerm));
  }
  // =========================
  // Shared helpers
  // =========================

  stopPropagation(event: Event): void {
    event.stopPropagation();
  }

  showCombinedEligibilityRules(event: Event): void {
    const rules = this.combinedEligibilityRulesMessage();
    if (!rules.length) return;
    this.combinedEligibilityPopover()?.show(event);
  }

  hideCombinedEligibilityRules(): void {
    this.combinedEligibilityPopover()?.hide();
  }

  addProspect() {
    this.#addProspectModalRef = this.#modalService.open(this.addProspectModal(), {
      ...this.#ngbModalService.centerModal,
      size: 'xl'
    });
  }

  closeProspectsResource() {
    this.prospectsResource.reload();
    this.#addProspectModalRef?.close();
    this.#addProspectModalRef = null;
  }
}
